import type { Keyboard, KeyboardLayout, QmkBoardLayer } from "@/domain/types";
import { findModifierCluster } from "@/hardware/geometry";
import { legendFor } from "@/hardware/keyboard-layouts/legends";
import { isBlank, keycodeForCode, keycodeInfo, keycodeLegend } from "./qmk-keycodes";

// The hand-side cluster's new emitted keycodes. Same-side suffix keeps a right-hand remap's
// codes (ControlRight, ...) distinct from the untouched left modifiers; a left-hand remap shares codes
// with the untouched right side, which twin-paints honestly (both physical keys really do emit it).
const SWAP = {
  right: { ctrl: "KC_RCTL", alt: "KC_RALT", shift: "KC_RSFT" },
  left: { ctrl: "KC_LCTL", alt: "KC_LALT", shift: "KC_LSFT" },
} as const;

/**
 * Build TheCore Plus's derived modifier-remap board layer over the viewer's own Keyboard +
 * Keyboard Layout. On the keyboard-hand's side it relocates the cluster - Shift emits Control, Control
 * emits Alt, the key next to Control emits Shift, and that side's Alt goes dead - while every other key
 * keeps its identity (its active code is its own KeyboardEvent.code) and the active layout's char. It
 * is just another active-code board layer, so projection and resolution need no change: the
 * remapped keys legend and resolve as the code they now emit. Returns null when the board has no clean
 * hand-side cluster (the Plus-incompatible case), so the caller can gate or annotate.
 */
export function deriveModRemapLayer(
  keyboard: Keyboard,
  layout: KeyboardLayout,
  hand: "left" | "right",
): QmkBoardLayer | null {
  const cluster = findModifierCluster(keyboard, hand);
  if (!cluster) return null;
  const swap = SWAP[hand];
  const keycodes: string[] = [];
  const chars: (string | null)[] = [];
  const legends: string[] = [];
  keyboard.keys.forEach((key, i) => {
    const code = key.code;
    if (i === cluster.shift || i === cluster.ctrl || i === cluster.neighbor || i === cluster.alt) {
      // a remapped modifier (or the dead Alt): emit the swapped keycode, no char, modifier-label legend
      const kc =
        i === cluster.shift
          ? swap.ctrl
          : i === cluster.ctrl
            ? swap.alt
            : i === cluster.neighbor
              ? swap.shift
              : "KC_NO"; // cluster.alt: dead
      keycodes.push(kc);
      chars.push(null);
      legends.push(keycodeLegend(kc));
    } else {
      // every other key keeps its identity and the active layout's char; its legend tracks the layout
      // (so AZERTY's KeyQ reads "A"), matching exactly what the universal projection would render.
      const char = layout.map[code] ?? null;
      keycodes.push(keycodeForCode(code) ?? "");
      chars.push(char);
      legends.push(char ?? legendFor(code));
    }
  });
  return {
    kind: "qmk",
    id: `plus-${hand}`,
    name: `TheCore Plus (${hand})`,
    keycodes,
    // the ACTIVE code each key emits through this derived layer, resolved once like a real
    // VIA/QMK layer: a remapped modifier reports its swapped code (ControlRight, ...), every other
    // key its own identity code - falling back to the geometry code where no keycode maps to it,
    // since a non-cluster key always keeps its real identity. null ONLY for the dead Alt (KC_NO).
    codes: keycodes.map((kc, i) =>
      isBlank(kc) ? null : (keycodeInfo(kc).code ?? keyboard.keys[i]?.code ?? null),
    ),
    chars,
    legends,
    empty: false,
    // carry the layout's VK-recovery tokens (and its ISO overflow) so the projection resolves
    // national OEM / digit tokens and 0045-displaced tokens the same way the universal path
    // does; the non-cluster keys keep their identity active code, so a token the table maps to
    // a code lands on that same physical key.
    ...(layout.tokens ? { tokens: layout.tokens } : {}),
    ...(layout.overflow ? { overflow: layout.overflow } : {}),
  };
}
